import { useEffect, useState } from 'react'

import { useMutation, useQuery } from '@tanstack/react-query'
import {
  createFileRoute,
  notFound,
  useNavigate,
  useParams,
} from '@tanstack/react-router'
import {
  ArrowLeft,
  Building2,
  Calendar,
  Clock,
  DollarSign,
  Link2,
  MapPin,
  Share2,
  Tag,
  Users,
} from 'lucide-react'
import { toast } from 'sonner'

import { Job } from '@/_gql/graphql'

import { useAuth } from '@/services/auth/use-auth'
import { GraphQLError } from '@/services/graphql/error'
import { gqlFetch } from '@/services/graphql/fetcher'
import { GqlAppyJob } from '@/services/job/gql'
import {
  queryGetStatusJobVacancy,
  queryJobList, // We'll use this to get single job for now
} from '@/services/job/query'

import Footer from '@/components/landing/footer'
import Navbar from '@/components/landing/navbar'
import { ButtonLoading } from '@/components/ui/button'

import { toIdrWithoutRp } from '@/lib/utils/number'
import { decodeUnicode } from '@/lib/utils/utils'

import { queryClient } from '@/lib/configs/tanstack-query'
import AlertDialogConfirmApply from '../_home-landing/-components/job-listing/-components/alert-dialog-confirm-apply'
import DialogMessageError from '../_home-landing/-components/job-listing/-components/dialog-message-error'

export const Route = createFileRoute('/job-detail/$jobId')({
  component: JobDetailComponent,
  beforeLoad: ({ params }) => {
    if (isNaN(Number(params.jobId))) {
      throw notFound()
    }
  },
})

function JobDetailComponent() {
  const { jobId } = useParams({ from: '/job-detail/$jobId' })
  const navigate = useNavigate()
  const user = useAuth((s) => s.user)

  const [isDialogErrorOpen, setIsDialogErrorOpen] = useState(false)
  const [dialogMessage, setDialogMessage] = useState('')
  const [isOpenDialogConfirm, setIsOpenDialogConfirm] = useState(false)
  const [jobDetail, setJobDetail] = useState<Job | null>(null)

  const TOAST_ID = 'ApplyJob'

  const {
    data: dataJobListing,
    isLoading,
    error,
  } = useQuery(queryJobList(true))

  // Apply job mutation (reusing existing logic)
  const { mutateAsync: applyJob, isPending: isApplying } = useMutation({
    mutationFn: async (job_id: number) => {
      await gqlFetch({
        query: GqlAppyJob,
        variables: {
          request: { job_id: job_id },
        },
      })
      return 1
    },
    onSuccess: () => {
      toast.success('Lamaran Anda Berhasil', { id: TOAST_ID })
      queryClient.refetchQueries(queryGetStatusJobVacancy())
    },
    onError: (error) => {
      let msg = error.message
      if (GraphQLError.is(error)) {
        msg = error.getMessage()
      }
      setDialogMessage(msg)
      setIsDialogErrorOpen(true)
    },
  })

  // Find specific job from the list
  useEffect(() => {
    if (dataJobListing && jobId) {
      const foundJob = (dataJobListing as Job[]).find(
        (job) => job.id === Number(jobId)
      )
      if (foundJob) {
        setJobDetail(foundJob)
      } else {
        throw notFound()
      }
    }
  }, [dataJobListing, jobId])

  const handleApplyJob = async () => {
    if (user?.id && jobDetail?.id) {
      setIsOpenDialogConfirm(true)
    } else {
      navigate({
        to: '/auth/login',
        search: { redirectTo: `/job-detail/${jobId}` },
      })
    }
  }

  const handleConfirmApply = async () => {
    if (jobDetail?.id) {
      await applyJob(jobDetail.id)
    }
  }

  const handleBackToJobs = () => {
    navigate({ to: '/', hash: '#job-listing' })
  }

  const handleCopyLink = () => {
    const jobUrl = `${window.location.origin}/job-detail/${jobId}`
    navigator.clipboard.writeText(jobUrl)
    toast.success('Link berhasil disalin!')
  }

  const handleShare = () => {
    if (navigator.share && jobDetail) {
      navigator
        .share({
          title: jobDetail.title,
          text: `Lihat lowongan kerja ${jobDetail.title} di YES Partner`,
          url: `${window.location.origin}/job-detail/${jobId}`,
        })
        .catch(() => {
          handleCopyLink()
        })
    } else {
      handleCopyLink()
    }
  }

  if (error) {
    throw notFound()
  }

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="flex min-h-screen items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-yes"></div>
        </div>
        <Footer />
      </>
    )
  }

  if (!jobDetail) {
    return (
      <>
        <Navbar />
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <h1 className="mb-4 text-2xl font-bold text-gray-900">
              Job tidak ditemukan
            </h1>
            <button
              onClick={handleBackToJobs}
              className="text-primary-yes hover:underline"
            >
              Kembali ke daftar lowongan
            </button>
          </div>
        </div>
        <Footer />
      </>
    )
  }

  const description = decodeUnicode(jobDetail?.description)

  return (
    <>
      <DialogMessageError
        msg={dialogMessage}
        open={isDialogErrorOpen}
        setOpen={setIsDialogErrorOpen}
      />

      <AlertDialogConfirmApply
        open={isOpenDialogConfirm}
        setOpen={setIsOpenDialogConfirm}
        handleApply={handleConfirmApply}
      />

      <Navbar className="relative" />

      <div className="bg-gray-50">
        {/* Header Section */}
        <div className="border-b bg-white">
          <div className="mx-auto max-w-4xl px-4 py-6 md:px-8">
            {/* Back Button */}
            <button
              onClick={handleBackToJobs}
              className="mb-6 flex items-center text-gray-600 transition-colors duration-300 hover:text-primary-yes"
            >
              <ArrowLeft size={20} className="mr-2" />
              Kembali ke Daftar Lowongan
            </button>

            {/* Job Header */}
            <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
              <div className="flex-1">
                {/* Company Logo */}
                <div className="mb-4 flex items-center gap-4">
                  <div>
                    <img
                      className="h-16 w-16"
                      src="/img/landing/logo.svg"
                      alt="Company Logo"
                    />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">
                      {jobDetail.title}
                    </h1>
                    <p className="text-lg font-medium text-gray-700">
                      YES Partner
                    </p>
                  </div>
                </div>

                {/* Key Details */}
                <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="flex items-center text-gray-600">
                    <MapPin size={18} className="mr-2 text-primary-yes" />
                    <span>{jobDetail.country.name}</span>
                  </div>

                  {/* {jobDetail.type && (
                    <div className="flex items-center text-gray-600">
                      <Clock size={18} className="mr-2 text-primary-yes" />
                      <span>{jobDetail.type}</span>
                    </div>
                  )} */}

                  {jobDetail.created_at && (
                    <div className="flex items-center text-gray-600">
                      <Calendar size={18} className="mr-2 text-primary-yes" />
                      <span>
                        Diposting{' '}
                        {new Date(jobDetail.created_at).toLocaleDateString(
                          'id-ID'
                        )}
                      </span>
                    </div>
                  )}
                </div>

                {/* Salary */}
                <div className="mb-6 rounded-lg border border-yes-200 bg-yes-100 p-4">
                  <div className="flex items-center text-yes-600">
                    <DollarSign size={18} className="mr-2" />
                    <span className="text-lg font-semibold">
                      Gaji: IDR{' '}
                      {`${toIdrWithoutRp(jobDetail?.min ?? 0)} - ${toIdrWithoutRp(jobDetail?.max ?? 0)}`}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 sm:flex-row md:flex-col">
                <div className="flex gap-2">
                  <button
                    onClick={handleCopyLink}
                    className="flex-1 rounded-lg border border-[#D4D4D4] px-4 py-3 transition-colors duration-300 hover:bg-gray-50 sm:flex-none"
                    title="Salin link"
                  >
                    <Link2 size={18} className="mx-auto" />
                  </button>

                  <button
                    onClick={handleShare}
                    className="flex-1 rounded-lg border border-[#D4D4D4] px-4 py-3 transition-colors duration-300 hover:bg-gray-50 sm:flex-none"
                    title="Bagikan"
                  >
                    <Share2 size={18} className="mx-auto" />
                  </button>
                </div>

                <ButtonLoading
                  onClick={handleApplyJob}
                  isLoading={isApplying}
                  className="rounded-xl bg-gradient-primary px-6 py-3 font-medium"
                >
                  Lamar Sekarang
                </ButtonLoading>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="mx-auto max-w-4xl px-4 py-8 md:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Job Skills */}
              {jobDetail.job_skills && jobDetail.job_skills.length > 0 && (
                <div className="mb-8 rounded-xl border border-[#D4D4D4] bg-white p-6 shadow-sm">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900">
                    Skills & Keahlian
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {jobDetail.job_skills.map((skill: any, index: any) => (
                      <span
                        key={index}
                        className="inline-flex items-center rounded-xl bg-[#FFF4F5] px-3 py-1 text-sm font-medium text-primary-yes"
                      >
                        <Tag size={12} className="mr-1" />
                        {skill.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Job Description */}
              <div className="mb-8 rounded-xl border border-[#D4D4D4] bg-white p-6 shadow-sm">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Deskripsi Pekerjaan
                </h3>
                <div className="prose prose-gray max-w-none">
                  {description ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: description }}
                      className="no-margin prose leading-relaxed text-gray-700"
                    />
                  ) : (
                    <p className="leading-relaxed text-gray-700">
                      Deskripsi pekerjaan tidak tersedia.
                    </p>
                  )}
                </div>
              </div>

              {/* Company Info */}
              <div className="rounded-xl border border-[#D4D4D4] bg-white p-6 shadow-sm">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Tentang Perusahaan
                </h3>
                <div className="flex items-start gap-4">
                  <img
                    className="h-16 w-16"
                    src="/img/landing/logo.svg"
                    alt="Company Logo"
                  />
                  <div>
                    <h4 className="mb-2 font-semibold text-gray-900">
                      YES Partner
                    </h4>
                    <p className="text-sm text-gray-600">
                      Perusahaan terpercaya yang menyediakan berbagai peluang
                      karir terbaik untuk pengembangan profesional Anda.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-6 rounded-xl border border-[#D4D4D4] bg-white p-6 shadow-sm">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">
                  Detail Pekerjaan
                </h3>

                <div className="space-y-4">
                  <div>
                    <p className="mb-1 text-sm text-gray-500">Lokasi</p>
                    <p className="flex items-center font-medium text-gray-900">
                      <MapPin size={16} className="mr-2 text-primary-yes" />
                      {jobDetail.country.name}
                    </p>
                  </div>

                  {/* {jobDetail.type && (
                    <div>
                      <p className="mb-1 text-sm text-gray-500">
                        Tipe Pekerjaan
                      </p>
                      <p className="flex items-center font-medium text-gray-900">
                        <Clock size={16} className="mr-2 text-primary-yes" />
                        {jobDetail.type}
                      </p>
                    </div>
                  )} */}

                  <div>
                    <p className="mb-1 text-sm text-gray-500">Gaji</p>
                    <p className="font-semibold text-yes-600">
                      IDR{' '}
                      {`${toIdrWithoutRp(jobDetail?.min ?? 0)} - ${toIdrWithoutRp(jobDetail?.max ?? 0)}`}
                    </p>
                  </div>

                  <div>
                    <p className="mb-1 text-sm text-gray-500">
                      Tanggal Posting
                    </p>
                    <p className="font-medium text-gray-900">
                      {jobDetail.created_at &&
                        new Date(jobDetail.created_at).toLocaleDateString(
                          'id-ID'
                        )}
                    </p>
                  </div>
                </div>

                {/* Apply Button (Mobile Sticky) */}
                <div className="mt-6 border-t border-gray-200 pt-4">
                  <ButtonLoading
                    onClick={handleApplyJob}
                    isLoading={isApplying}
                    className="w-full rounded-xl bg-gradient-primary px-6 py-3 font-medium"
                  >
                    Lamar Sekarang
                  </ButtonLoading>

                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={handleCopyLink}
                      className="flex-1 rounded-lg border border-[#D4D4D4] px-4 py-2 text-sm transition-colors duration-300 hover:bg-gray-50"
                      title="Salin link"
                    >
                      <Link2 size={16} className="mx-auto" />
                    </button>

                    <button
                      onClick={handleShare}
                      className="flex-1 rounded-lg border border-[#D4D4D4] px-4 py-2 text-sm transition-colors duration-300 hover:bg-gray-50"
                      title="Bagikan"
                    >
                      <Share2 size={16} className="mx-auto" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  )
}
